import React, { useState, useEffect } from 'react';
import { Star, ShoppingCart, Sparkles } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { api } from '../config/api';

const Menu = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [toast, setToast] = useState('');
  const location = useLocation();

  useEffect(() => {
    fetchMenu();
  }, []);

  const fetchMenu = async () => {
    try {
      const response = await api.get('/menu.php');
      if (response.data.success) {
        setMenuItems(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching menu:', error);
    }
  };

  const categories = ['All', ...new Set(menuItems.map(item => item.category))];

  const searchParams = new URLSearchParams(location.search);
  const searchTarget = searchParams.get('search') || '';

  const filteredItems = menuItems.filter(item => {
    const matchesCategory = selectedCategory === 'All' || item.category === selectedCategory;
    const matchesSearch = !searchTarget || 
      item.item_name.toLowerCase().includes(searchTarget.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTarget.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const addToCart = (item) => {
    const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');
    const existingIndex = existingCart.findIndex(cartItem => cartItem.menu_id === item.menu_id);
    
    let updatedCart;
    if (existingIndex >= 0) {
      updatedCart = [...existingCart];
      updatedCart[existingIndex] = { 
        ...updatedCart[existingIndex], 
        quantity: updatedCart[existingIndex].quantity + 1 
      };
    } else {
      updatedCart = [...existingCart, { ...item, quantity: 1 }];
    }
    
    localStorage.setItem('cart', JSON.stringify(updatedCart));
    window.dispatchEvent(new Event('cartUpdated'));
    setToast(`${item.item_name} added to cart!`);
    setTimeout(() => setToast(''), 2500);
  };

  return (
    <div>
      {/* Add-to-cart toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: '90px', right: '20px', zIndex: 1080,
          backgroundColor: '#1A1A1A', color: '#fff',
          padding: '14px 22px', borderRadius: '10px',
          boxShadow: '0 8px 30px rgba(0,0,0,0.25)',
          borderLeft: '4px solid #4CAF50',
          fontSize: '0.9rem', fontWeight: 500,
          display: 'flex', alignItems: 'center', gap: '10px'
        }}>
          <ShoppingCart size={16} color="#4CAF50" /> {toast}
        </div>
      )}

      {/* Menu Header */}
      <section className="section-padding text-center" style={{
        background: 'linear-gradient(rgba(26, 26, 26, 0.8), rgba(26, 26, 26, 0.9)), url("https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1920&q=80")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        color: '#ffffff'
      }}>
        <div className="container">
          <span className="section-subtitle" style={{ color: '#D4AF37' }}>Culinary Excellence</span>
          <h1 className="mt-3 mb-4" style={{ fontFamily: 'Playfair Display, serif', fontSize: '3.5rem', fontWeight: 600, color: '#ffffff' }}>
            Our Menu
          </h1>
          <div className="gold-divider-center" style={{ background: '#D4AF37' }}></div>
          <p style={{ fontSize: '1.1rem', maxWidth: '600px', margin: '20px auto 0', lineHeight: 1.8, color: '#ffffff' }}>
            Discover our carefully crafted dishes, prepared with passion and the finest ingredients
          </p>
        </div>
      </section>

      {/* Menu Content */}
      <section className="section-padding" style={{ backgroundColor: '#faf8f5' }}>
        <div className="container">
          {/* Category Filter */}
          <div className="d-flex flex-wrap gap-2 justify-content-center mb-5">
            {categories.map(category => (
              <button
                key={category}
                className="btn"
                style={{
                  backgroundColor: selectedCategory === category ? '#4CAF50' : 'transparent',
                  color: selectedCategory === category ? '#ffffff' : '#555',
                  border: selectedCategory === category ? 'none' : '1px solid #e0e0e0',
                  padding: '10px 28px',
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  borderRadius: '30px',
                  transition: 'all 0.3s ease',
                  boxShadow: selectedCategory === category ? '0 4px 15px rgba(76, 175, 80, 0.3)' : 'none'
                }}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Menu Items Grid */}
          <div className="row g-4">
            {filteredItems.map(item => (
              <div key={item.menu_id} className="col-md-6 col-lg-4">
                <div className="card h-100 hover-lift" style={{
                  borderRadius: '16px', overflow: 'hidden',
                  boxShadow: '0 4px 25px rgba(0,0,0,0.06)',
                  backgroundColor: '#ffffff'
                }}>
                  {item.image_url ? (
                    <div className="img-zoom">
                      <img
                        src={item.image_url}
                        alt={item.item_name}
                        className="card-img-top"
                        style={{ height: '240px', objectFit: 'cover' }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `<div style="height:240px;background:linear-gradient(135deg,#f5f5f5,#e8e8e8);display:flex;align-items:center;justify-content:center"><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" stroke-width="1.5" opacity="0.4"><path d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"/></svg></div>`;
                        }}
                      />
                    </div>
                  ) : (
                    <div style={{
                      height: '240px',
                      background: 'linear-gradient(135deg, #f5f5f5, #e8e8e8)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <Sparkles size={40} color="#D4AF37" opacity={0.4} />
                    </div>
                  )}

                  {/* Category Badge */}
                  <div style={{
                    position: 'absolute', top: '16px', left: '16px',
                    backgroundColor: 'rgba(26,26,26,0.8)', backdropFilter: 'blur(10px)',
                    padding: '5px 14px', borderRadius: '20px',
                    color: '#D4AF37', fontSize: '0.7rem', fontWeight: 600,
                    letterSpacing: '1px', textTransform: 'uppercase'
                  }}>
                    {item.category}
                  </div>

                  <div className="card-body p-4">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h5 className="fw-bold mb-0" style={{
                        fontFamily: 'Playfair Display, serif',
                        fontSize: '1.2rem', fontWeight: 600
                      }}>
                        {item.item_name}
                      </h5>
                    </div>

                    {/* Rating */}
                    <div className="d-flex align-items-center gap-1 mb-3">
                      <div className="d-flex gap-0">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} size={13} fill={i < 4 ? '#D4AF37' : 'none'} color="#D4AF37" />
                        ))}
                      </div>
                      <span style={{ color: '#999', fontSize: '0.8rem' }}>(4.8)</span>
                    </div>

                    <p style={{ color: '#888', fontSize: '0.88rem', lineHeight: 1.7, marginBottom: '20px' }}>
                      {item.description}
                    </p>

                    <div className="d-flex justify-content-between align-items-center mt-auto">
                      <h4 className="fw-bold mb-0" style={{
                        color: '#4CAF50',
                        fontFamily: 'Playfair Display, serif',
                        fontSize: '1.3rem'
                      }}>
                        UGX {Number(item.price).toLocaleString()}
                      </h4>
                      <button
                        className="btn btn-gold d-flex align-items-center gap-2"
                        style={{
                          padding: '10px 22px',
                          fontSize: '0.8rem',
                          letterSpacing: '1px',
                          borderRadius: '8px'
                        }}
                        onClick={() => addToCart(item)}
                      >
                        <ShoppingCart size={14} />
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Menu;
